<template>
  <v-dialog v-model="isOpen" max-width="1000px" persistent>
    <v-card class="pa-4" id="form-scroll-container">
      <v-card-title class="headline text-orange-darken-2 pb-2">
        <div class="d-flex align-center">
          <v-icon left class="mr-2" color="#1D1B59">fas fa-plus</v-icon>
          {{ $t("views.trips.create_trip.create_stop") }}
        </div>
        <v-btn icon @click="$emit('close')" class="ml-auto">
          <v-icon color="#1D1B59">fas fa-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider class="mb-4" />

      <v-card-text>
        <v-row dense>
          <v-col cols="12">
            <v-text-field v-model="formData.number_order" :label="$t('views.trips.create_trip.number_order')" outlined
              dense placeholder="Ej: PED-001-AX" />
          </v-col>
          <v-col cols="12" md="6">
            <h4 class="text-subtitle-1 font-weight-bold mb-2">
              <v-icon left class="mr-2" color="#1D1B59">fas fa-map-marker-alt</v-icon>
              {{ $t("views.trips.create_trip.stop_information") }}
            </h4>
            <v-radio-group v-model="formData.type_location" row :rules="[rules.required]">
              <v-radio :label="$t('views.trips.create_trip.origin')" value="origen" />
              <v-radio :label="$t('views.trips.create_trip.destination')" value="destino" />
            </v-radio-group>
            <v-row dense>
              <v-col cols="6">
                <v-combobox v-model="formData.description_company_client" :items="FilteredClients"
                  :label="$t('views.trips.create_trip.client_desc')" outlined dense clearable :data-scroll-id="'client'"
                  :search-input.sync="searchClient" @focus="setActiveScroll('client')" @change="onClientDescriptionSelected" />
              </v-col>
              <v-col cols="6">
                <v-text-field v-model="formData.id_company_client" :label="$t('views.trips.create_trip.client_id')"
                  outlined dense :readonly="clientDescriptionExists" />
              </v-col>
            </v-row>
            <v-row dense>
              <v-col cols="12">
                <v-combobox v-model="formData.description_location_destiny" :items="FilteredLocations"
                  :label="$t('views.trips.create_trip.location_code')" outlined dense clearable
                  :data-scroll-id="'location'" :search-input.sync="searchLocation" @change="onLocationSelected"
                  @focus="setActiveScroll('location')" />
              </v-col>
              <v-col cols="6">
                <v-text-field v-model="formData.code_location_destiny"
                  :label="$t('views.trips.create_trip.code_location')" outlined dense :readonly="localityExists" />
              </v-col>
              <v-col cols="6">
                <v-text-field v-model="formData.latitude" :label="$t('views.trips.create_trip.latitude')" outlined dense
                  :readonly="localityExists" />
              </v-col>
              <v-col cols="6">
                <v-text-field v-model="formData.longitude" :label="$t('views.trips.create_trip.longitude')" outlined
                  dense :readonly="localityExists" />
              </v-col>
              <!-- PAÍS -->
              <v-col cols="6">
                <v-combobox v-model="formData.name_country" :items="countries.map(c => c.name)"
                  :label="$t('views.trips.create_trip.name_country')" outlined dense clearable
                  :readonly="localityExists" @focus="loadCountries" @change="handleCountrySelected" />
              </v-col>

              <!-- DEPARTAMENTO -->
              <v-col cols="6">
                <v-combobox v-model="formData.name_deparment" :items="departments.map(c => c.name)"
                  :label="$t('views.trips.create_trip.name_deparment')" outlined dense clearable
                  :readonly="localityExists" @focus="loadDepartments" @change="handleDepartmentSelected" />
              </v-col>

              <!-- CIUDAD -->
              <v-col cols="6">
                <v-combobox v-model="formData.description_city" :items="cities.map(c => c.name)"
                  :label="$t('views.trips.create_trip.name_city')" outlined dense clearable :readonly="localityExists"
                  @focus="loadCities" @change="handleCitySelected" />
              </v-col>

            </v-row>
            <v-row dense>
              <v-col cols="12">
                <v-text-field v-model="formData.datetime_in_place" :label="$t('views.trips.create_trip.scheduled_date')"
                  type="datetime-local" outlined dense :min="minDateTime" />
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="12" md="6">
            <h4 class="text-subtitle-1 font-weight-bold mb-2">
              <v-icon small left class="mr-2" color="#1D1B59">fas fa-map</v-icon>
              {{ $t("views.trips.create_trip.stop") }}
            </h4>

            <v-list v-if="stops.length" dense id="form-scroll-stops">
              <v-list-item v-for="(stops, index) in stops" :key="index" class="mb-2">
                <v-list-item-content>
                  <v-row no-gutters>
                    <v-col cols="8">
                      <v-list-item-title>
                        {{ $t(`views.trips.create_trip.stop`) }}: {{ stops.type_location }}
                        {{ stops.description_company_client }} - {{ stops.code_location_destiny }} ({{
                          stops.description_location_destiny }})
                      </v-list-item-title>
                      <v-list-item-subtitle>
                        {{ $t("views.trips.create_trip.scheduled_date") }}: {{ stops.datetime_in_place }} -
                        {{ $t("views.trips.create_trip.remission") }}: {{ stops.remission }} -
                        {{ $t("views.trips.create_trip.shipment_number") }}: {{ stops.shipment_number }} -
                        {{ $t("views.trips.create_trip.goods_status") }}: {{ stops.description_state_order }}
                      </v-list-item-subtitle>
                    </v-col>

                    <v-col cols="4" class="d-flex justify-end align-center">
                      <v-btn icon small @click="editStop(index)">
                        <v-icon small color="primary">fas fa-pen</v-icon>
                      </v-btn>
                      <v-btn icon small @click="removeStop(index)">
                        <v-icon small color="error">fas fa-trash</v-icon>
                      </v-btn>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="6" class="pr-0 pt-0">
                      <v-btn text small block class="mt-1" @click="toggleShowMore(index)">
                        <v-icon class="mr-1" small>
                          {{ expandedStops.includes(index) ? 'fas fa-chevron-up' : 'fas fa-chevron-down' }}
                        </v-icon>
                        {{ $t(expandedStops.includes(index) ? 'views.trips.create_trip.view_less' :
                          'views.trips.create_trip.view_more') }}
                      </v-btn>
                    </v-col>

                    <v-col cols="6" class="pl-0 pt-0">
                      <v-btn text small block color="primary" class="mt-1" @click="viewProducts(stops.products)">
                        <v-icon class="mr-1" small>fas fa-box-open</v-icon>
                        {{ $t("views.trips.create_trip.view_products") }}
                      </v-btn>
                    </v-col>
                  </v-row>


                  <v-list v-if="expandedStops.includes(index) && stops.products && stops.products.length" dense
                    class="mt-2 ml-4">
                    <v-list-item v-for="(products, i) in stops.products" :key="i" class="pl-1">
                      <v-list-item-content>
                        <v-list-item-title class="font-weight-medium">
                          {{ products.description_product }} ({{ products.code_product }})
                        </v-list-item-title>

                        <v-list-item-subtitle>
                          {{ $t('views.trips.create_trip.barcode') }}: {{ products.barcode }}
                        </v-list-item-subtitle>

                        <v-list-item-subtitle>
                          {{ $t('views.trips.create_trip.amount_order') }}: {{ products.amount_order }} |
                          {{ $t('views.trips.create_trip.amount_deliver') }}: {{ products.amount_deliver }} |
                          {{ $t('views.trips.create_trip.unit') }}: {{ products.unit }}
                        </v-list-item-subtitle>

                        <v-list-item-subtitle>
                          {{ $t('views.trips.create_trip.routing_variable') }}: {{ products.routing_variable }}
                        </v-list-item-subtitle>

                      </v-list-item-content>
                      <v-list-item-action>
                        <div class="d-flex align-center">
                          <v-btn icon small @click="editProduct(products, index, i)">
                            <v-icon small color="primary">fas fa-pen</v-icon>
                          </v-btn>
                          <v-btn icon small @click="deleteProduct(index, i)">
                            <v-icon small color="error">fas fa-trash</v-icon>
                          </v-btn>
                        </div>
                      </v-list-item-action>
                    </v-list-item>
                  </v-list>
                </v-list-item-content>
              </v-list-item>
            </v-list>
            
            <v-alert v-else type="info" class="mt-2">
              {{ $t("views.trips.create_trip.no_stops_added") }}
            </v-alert>
          </v-col>
          <v-divider class="mb-4" />
        </v-row>
          <v-row dense>
          <v-col cols="6">
            <v-text-field v-model="formData.remission" :label="$t('views.trips.create_trip.remission')" outlined
              dense />
          </v-col>
          <v-col cols="6">
            <v-text-field v-model="formData.shipment_number" :label="$t('views.trips.create_trip.shipment_number')"
              outlined dense />
          </v-col>
          <v-col cols="6">
            <v-combobox v-model="formData.businessUnit" :items="businessUnits.map(b => b.description)"
              :label="$t('views.trips.create_trip.business_description')" outlined dense clearable
              @focus="loadBusinessUnits" @change="handleBusinessUnitSelected" />
          </v-col>
          <v-col cols="6">
            <v-text-field v-model="formData.code_businessunit" :label="$t('views.trips.create_trip.business_unit')"
              outlined dense readonly />
          </v-col>
          <v-col cols="6">
            <v-combobox v-model="formData.name_location_seller" :items="salesChannels.map(c => c.description)"
              :label="$t('views.trips.create_trip.sales_description')" outlined dense clearable
              @focus="loadSalesChannels" @change="handleSalesChannelSelected" />
          </v-col>
          <v-col cols="6">
            <v-text-field v-model="formData.code_location_seller" :label="$t('views.trips.create_trip.sales_channel')"
              outlined dense :readonly="salesChannelExist" />
          </v-col>
          <v-col cols="6">
            <v-combobox v-model="formData.name_zone" :items="productTypes.map(p => p.description)"
              :label="$t('views.trips.create_trip.product_type')" outlined dense clearable @focus="loadProductTypes"
              @change="handleProductTypeSelected" />
          </v-col>
          <v-col cols="6">
            <v-text-field v-model="formData.code_zone" :label="$t('views.trips.create_trip.product_type_code')" outlined
              dense :readonly="ProductTypeExists" />
          </v-col>
          <v-col cols="6">
            <v-combobox v-model="formData.type_commodity" :items="commodityTypes.map(c => c.description)"
              :label="$t('views.trips.create_trip.goods_type')" outlined dense clearable @focus="loadCommodityTypes"
              @change="handleCommodityTypeSelected" />
          </v-col>
          <v-col cols="6">
            <v-text-field v-model="formData.code_type_commodity" :label="$t('views.trips.create_trip.goods_type_code')"
              outlined dense :readonly="MerchandiseTypeExists" />
          </v-col>
          <v-col cols="12">
            <v-autocomplete v-model="formData.description_state_order" :items="statesMerchandise"
              :label="$t('views.trips.create_trip.goods_status')" outlined dense clearable />
          </v-col>
          <v-col cols="4">
            <v-btn color="orange darken-2" block class="mt-3 py-5" style="color: #ffffff" @click="openProductModal()">
              <v-icon small class="mr-2">fas fa-box</v-icon>
              {{ $t("views.trips.create_trip.add_product") }}
            </v-btn>
          </v-col>
          <v-col cols="4">
            <v-btn color="orange darken-2" block class="mt-3 py-5" style="color: #ffffff" @click="openAllProductsModal">
              <v-icon left>fas fa-boxes</v-icon>
              {{ $t("views.trips.create_trip.view_all_products") }}
            </v-btn>
          </v-col>
          <v-col cols="4">
            <v-btn color="orange darken-2" block class="mt-3 py-5" @click="addStop" style="color: #ffffff" :disabled="!formData.id_company_client ||
              !formData.code_location_destiny ||
              !formData.datetime_in_place || !formData.type_location">
              <v-icon small left class="mr-2">fas fa-plus</v-icon>
              {{ $t("views.trips.create_trip.add_to_stop") }}
            </v-btn>

            <p class="mt-2 text-caption grey--text font-italic">
              ** {{ $t("views.trips.create_trip.remember_add_destination") }}
            </p>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions class="justify-end">
        <v-btn text color="grey darken-1" @click="$emit('close')" class="py-5">
          {{ $t("views.trips.create_trip.cancel") }}
        </v-btn>
        <v-btn color="#1D1B59" class="py-5" @click="confirmRoute" style="color: #ffffff">
          {{ $t("views.trips.create_trip.confirm") }}
        </v-btn>
      </v-card-actions>
    </v-card>

    <EditStopModal :isOpen="showEditStopModal" :initialStop="stopEditing" @save="onStopEdited"
      @close="showEditStopModal = false" :statesMerchandise="statesMerchandise" :idCompanyParent="idCompanyParent" />
    <EditProductModal :isOpen="showEditProductModal" :initialProduct="productEditing" @save="onProductEdited"
      @close="showEditProductModal = false" :idCompanyParent="idCompanyParent" />
    <AddProductModal :isOpen="showProductForm" :initialProduct="currentProduct" @save="saveProductFromModal"
      @close="showProductForm = false" :idCompanyParent="idCompanyParent" />
    <ViewProductsModal :isOpen="viewProductsModalOpen" :products="productsToView"
      @close="viewProductsModalOpen = false" />
    <ViewProductsModal :isOpen="viewAllProductsModalOpen" :products="allProducts"
      @close="viewAllProductsModalOpen = false" />

  </v-dialog>
</template>

<script>

import EditStopModal from "@/components/views/Trip/Create/Modals/EditModals/EditStopModal.vue";
import EditProductModal from "@/components/views/Trip/Create/Modals/EditModals/EditProductModal.vue";
import AddProductModal from "@/components/views/Trip/Create/Modals/AddModals/AddProductModal.vue";
import debounce from 'lodash/debounce';
import { formatDateTimeForBackend } from "@/plugins/dateUtils";
import ViewProductsModal from "@/components/views/Trip/Create/Modals/ViewProductsModal.vue";

export default {
  components: {
    EditStopModal,
    EditProductModal,
    AddProductModal,
    ViewProductsModal
  },
  name: "AddStopModal",
  props: {
    numberMainTravel: {
      type: String,
      default: "",
    },
    isOpen: {
      type: Boolean,
      required: true
    },
    idCompanyParent: {
      type: Number,
      required: true,
    }
  },
  emits: ["close", "add"],
  data() {

    return {
      countries: [],
      departments: [],
      cities: [],
      viewProductsModalOpen: false,
      productsToView: [],
      viewAllProductsModalOpen: false,
      allProducts: [],

      activeScrollId: null,
      loadingScroll: {
        location: false,
        client: false,
      },
      formData: {
        number_order: "",
        address: "",
        businessUnit: "",
        code_businessunit: "",
        code_city: "",
        description_city: "",
        code_deparment: "",
        name_deparment: "",
        code_country: "",
        name_country: "",
        code_location_destiny: "",
        code_location_seller: "",
        code_zone: "",
        datetime_in_place: "",
        datetime_out_place: null,
        description_company_client: "",
        description_location_destiny: "",
        id_company_client: "",
        indications: "",
        latitude: "",
        longitude: "",
        name_location_seller: "",
        name_zone: "",
        remission: "",
        shipment_number: "",
        timezone: "UTC-05:00",
        type_commodity: "",
        type_company_client: "",
        description_type_delivery: "",
        type_location: "",
        zip: "",
        description_state_order: "",
        id_location: null,
        products: [],
        code_type_commodity: "",
        id_city: 0,

      },
      idCountry: null,
      idDepartment: null,
      stops: [],
      businessUnits: [],
      businessUnitsLoaded: false,
      salesChannels: [],
      salesChannelsLoaded: false,
      productTypes: [],
      commodityTypes: [],
      productTypesLoaded: false,
      commodityTypesLoaded: false,
      expandedStops: [],
      stopEditing: null,
      productEditing: null,
      indexEditing: null,
      indexEditingStop: null,
      // Modales y controles
      showEditStopModal: false,
      showEditProductModal: false,
      showProductForm: false,
      currentProduct: {},
      // Estados y validaciones
      localityExists: true,
      clientDescriptionExists: true,
      ProductTypeExists: true,
      MerchandiseTypeExists: true,
      salesChannelExist: true,
      businessUnitExist: true,
      // Clientes
      clients: [],
      clientPage: 1,
      totalClientPages: null,
      searchClient: "",
      // Localidades
      locations: [],
      locationPage: 1,
      totalLocationPages: null,
      searchLocation: "",
      statesMerchandise: ["Ordenado", "Solicitado", "Cancelado", "Pendiente"],
      rules: {
        required: (value) => !!value || this.$t("views.trips.create_trip.required"),
      },
    };
  },
  watch: {
    searchLocation: {
      handler: debounce(function (value) {
        if (!this.formData.id_company_client) return;

        this.locationPage = 1;
        this.locations = [];

        const description = value?.trim() || "";
        this.loadLocations(description);
      }, 500),
      immediate: false,
    },
    searchClient: {
      handler: debounce(function (value) {
        this.clientPage = 1;
        this.clients = [];

        const search = value?.trim() || "";
        this.loadClients(search);
      }, 500),
      immediate: false,
    }
  },
  computed: {
    FilteredLocations() {
      const raw = this.searchLocation == null ? '' : this.searchLocation
      const search = String(raw).toLowerCase().trim()
      const list = this.locations
        .filter(l => l.description.toLowerCase().includes(search))
        .map(l => l.description);
      return list.length
        ? list
        : [`${this.searchLocation}`];
    },
    FilteredClients() {
      const raw = this.searchClient == null ? '' : this.searchClient
      const search = String(raw).toLowerCase().trim()
      const list = this.clients
        .filter(c => c.businessName.toLowerCase().includes(search))
        .map(c => c.businessName);
      return list.length
        ? list
        : [`${this.searchClient}`];
    },
    clientNames() {
      return this.clients.map(c => c.businessName);
    }


  },
  async mounted() {
    try {
      await Promise.all([
        this.loadClients(),
        this.setMinDateTime(),
      ]);
    } catch (e) {
      console.error(" load error:", e);
    }
  },
  methods: {
    async loadCountries() {
      if (this.countries.length === 0) {
        try {
          const res = await this.axios.get("/Country/GetAll");
          this.countries = res.data || [];
          if (this.countries.length === 0) {
            console.warn("No countries found");
          }
        } catch (error) {
          console.error("Error loading countries:", error);
          this.countries = [];
          // You could show a user-friendly message here
          // this.$toast.error('Error al cargar los países');
        }
      }
    },
    async loadDepartments() {
      if (!this.idCountry) return;
      try {
        const res = await this.axios.get(`/Departments/GetByCountry/${this.idCountry}`);
        this.departments = res.data || [];
      } catch (error) {
        console.error("Error loading departments:", error);
        this.departments = [];
      }
    },
    async loadCities() {
      if (!this.idDepartment) return;
      try {
        const res = await this.axios.get(`/City/ByIdDepartment/${this.idDepartment}`);
        this.cities = res.data || [];
      } catch (error) {
        console.error("Error loading cities:", error);
        this.cities = [];
      }
    },
    handleCountrySelected(name) {
      const selected = this.countries.find(c => c.name === name);
      if (selected) {
        this.formData.name_country = selected.name;
        this.formData.code_country = selected.code;
        this.idCountry = selected.idCountry;
        
        // Reset dependent fields
        this.formData.name_deparment = '';
        this.formData.code_deparment = '';
        this.formData.description_city = '';
        this.formData.code_city = '';
        this.formData.id_city = null;
        this.idDepartment = null;
        
        // Clear dependent arrays
        this.departments = [];
        this.cities = [];
        
        // Load departments for selected country
        this.loadDepartments();
      } else {
        this.formData.name_country = name || '';
        this.formData.code_country = '';
        this.idCountry = null;
        
        // Reset dependent fields when clearing country
        this.formData.name_deparment = '';
        this.formData.code_deparment = '';
        this.formData.description_city = '';
        this.formData.code_city = '';
        this.formData.id_city = null;
        this.idDepartment = null;
        
        // Clear dependent arrays
        this.departments = [];
        this.cities = [];
        
        // Only set localityExists to false if we're not editing an existing location
        // This prevents unlocking fields when just clearing the country field
        if (!this.formData.id_location) {
          this.localityExists = false;
        }
      }
    },
    handleDepartmentSelected(name) {
      const selected = this.departments.find(d => d.name === name);
      if (selected) {
        this.formData.name_deparment = selected.name;
        this.formData.code_deparment = selected.code;
        this.idDepartment = selected.idDepartments;
        
        // Reset dependent fields
        this.formData.description_city = '';
        this.formData.code_city = '';
        this.formData.id_city = null;
        
        // Clear dependent arrays
        this.cities = [];
        
        // Load cities for selected department
        this.loadCities();
      } else {
        this.formData.name_deparment = name || '';
        this.formData.code_deparment = '';
        this.idDepartment = null;
        
        // Reset dependent fields when clearing department
        this.formData.description_city = '';
        this.formData.code_city = '';
        this.formData.id_city = null;
        
        // Clear dependent arrays
        this.cities = [];
        
        // Only set localityExists to false if we're not editing an existing location
        if (!this.formData.id_location) {
          this.localityExists = false;
        }
      }
    },
    handleCitySelected(name) {
      const selected = this.cities.find(c => c.name === name);
      if (selected) {
        this.formData.description_city = selected.name;
        this.formData.code_city = selected.codeCity;
        this.formData.id_city = selected.idCity;
        
        // If we have latitude and longitude from the city, use them
        if (selected.lat && selected.lon) {
          this.formData.latitude = selected.lat;
          this.formData.longitude = selected.lon;
        }
      } else {
        this.formData.description_city = name || '';
        this.formData.code_city = '';
        this.formData.id_city = null;
        
        // Only set localityExists to false if we're not editing an existing location
        if (!this.formData.id_location) {
          this.localityExists = false;
        }
      }
    },

    viewProducts(products) {
      this.productsToView = products;
      this.viewProductsModalOpen = true;
    },
    openAllProductsModal() {
      const mergedFromStops = this.stops.flatMap(stop => stop.products || []);
      const currentProducts = this.formData.products || [];

      const merged = [...mergedFromStops, ...currentProducts];

      this.allProducts = merged;
      this.viewAllProductsModalOpen = true;
    },
    setMinDateTime() {
      const now = new Date();
      const offset = -now.getTimezoneOffset();
      now.setMinutes(now.getMinutes() + offset);

      const pad = (n) => (n < 10 ? '0' + n : n);
      this.minDateTime = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
    },
    handleResetStops() {
      this.stops = [];
    },
    setActiveScroll(id) {
      this.activeScrollId = id;

      if (id === 'location' && this.locations.length === 0) {
        this.loadLocations();
      }

      this.pollForDropdownScrollListener(id);
    },


    pollForDropdownScrollListener(id) {
      const interval = setInterval(() => {
        const menus = document.querySelectorAll('.v-menu__content');

        menus.forEach(menu => {
          const alreadyAttached = menu.dataset[`scrollAttached_${id}`];
          if (alreadyAttached) return;
          if (this.activeScrollId !== id) return;
          menu.dataset[`scrollAttached_${id}`] = 'true';
          menu.addEventListener('scroll', (e) => this.handleScroll(e, id));
        });
      }, 500);

      setTimeout(() => {
        clearInterval(interval);
      }, 5000);
    },

    handleScroll(e, id) {
      const { scrollTop, scrollHeight, clientHeight } = e.target;

      if (scrollTop + clientHeight >= scrollHeight - 50) {
        if (this.loadingScroll[id]) {
          return;
        }

        this.loadingScroll[id] = true;

        if (id === 'location') {
          if (!this.totalLocationPages || this.locationPage < this.totalLocationPages) {
            this.locationPage++;
            this.loadLocations().finally(() => {
              this.loadingScroll[id] = false;
            });
          } else {
            this.loadingScroll[id] = false;
          }
        } if (id === 'client') {
          if (!this.totalClientPages || this.clientPage < this.totalClientPages) {
            this.clientPage++;
            const search = this.searchClient?.trim() || "";
            this.loadClients(search).finally(() => {
              this.loadingScroll[id] = false;
            });
          } else {
            this.loadingScroll[id] = false;
          }
        }


      }
    },

    async loadClients(search = "") {
      try {
        const size = 50;
        const page = this.clientPage;
        const baseUrl = `/Customer/Companies/${page}/${size}`;
        const url = search.trim()
          ? `${baseUrl}?search=${encodeURIComponent(search.trim())}`
          : baseUrl;

        const res = await this.axios.get(url);
        const data = res.data || [];

        if (data.length < size) this.totalClientPages = page;
        this.clients.push(...data);
      } catch (error) {
        console.error("Error loading clients:", error);
      }
    },

    async loadLocations(description = "") {
      try {
        const size = 50;
        const nit = this.formData.id_company_client;

        if (!nit) return;

        const page = this.locationPage;
        const baseUrl = `/Location/${nit}/${page}/${size}`;
        const url = description.trim()
          ? `${baseUrl}?description=${encodeURIComponent(description.trim())}`
          : baseUrl;

        const res = await this.axios.get(url);
        const data = res.data || [];

        if (data.length < size) this.totalLocationPages = page;
        this.locations.push(...data);
      } catch (error) {
        console.error("Error loading locations:", error);
      }
    },
    async loadBusinessUnits() {
      if (this.businessUnitsLoaded) return;

      try {
        const res = await this.axios.get(`/BusinessUnits/ByIdCompay/${this.idCompanyParent}`);
        this.businessUnits = res.data || [];
        this.businessUnitsLoaded = true;
      } catch (error) {
        console.error("Error loading business units:", error);
      }
    },
    async loadSalesChannels() {
      if (this.salesChannelsLoaded) return;

      try {
        const res = await this.axios.get(`/Channel/ByIdCompay/${this.idCompanyParent}`);
        this.salesChannels = res.data || [];
        this.salesChannelsLoaded = true;
      } catch (error) {
        console.error("Error loading sales channels:", error);
      }
    },
    async loadProductTypes() {
      if (this.productTypesLoaded) return;
      try {
        const res = await this.axios.get(`/Product/TypeProduct/${this.idCompanyParent}`);
        this.productTypes = res.data || [];
        this.productTypesLoaded = true;
      } catch (err) {
        console.error("Error loading product types:", err);
      }
    },

    async loadCommodityTypes() {
      if (this.commodityTypesLoaded) return;
      try {
        const res = await this.axios.get(`/Merchandise/GetAll/${this.idCompanyParent}`);
        this.commodityTypes = res.data || [];
        this.commodityTypesLoaded = true;
      } catch (err) {
        console.error("Error loading commodity types:", err);
      }
    },

    addStop() {

      // if (!this.formData.number_order?.trim()) {
      //   this.formData.number_order = this.numberMainTravel;
      // }

      const newStop = {
        ...this.formData,
        code_type_commodity: this.formData.code_type_commodity || "",
      };

      this.stops.push(newStop);
      this.resetForm();
    },
    confirmRoute() {
      this.$emit("add", this.stops);
      this.$emit("close");
    },
    removeStop(index) {
      this.stops.splice(index, 1);
    },
    editStop(index) {
      this.stopEditing = { ...this.stops[index] };
      this.indexEditingStop = index;
      this.showEditStopModal = true;
    },
    onStopEdited(updatedStop) {
      if (this.indexEditingStop !== null) {
        this.$set(this.stops, this.indexEditingStop, { ...updatedStop });
        this.indexEditingStop = null;
      }
      this.showEditStopModal = false;
    },
    onClientDescriptionSelected(value) {
      const cliente = this.clients.find(c => c.businessName === value);
      this.formData.id_company_client = cliente?.identification || "";
      this.formData.type_company_client = cliente?.typeCompanie || "";
      this.clientDescriptionExists = !!cliente;
      this.locations = [];
      this.locationPage = 1;
      this.totalLocationPages = null;
      this.loadLocations();
    },
    handleProductTypeSelected(description) {
      const selected = this.productTypes.find(p => p.description === description);
      if (selected) {
        this.formData.name_zone = selected.description;
        this.formData.code_zone = selected.code;
        this.ProductTypeExists = true;
      } else {
        this.formData.name_zone = description;
        if (!this.formData.code_zone) {
          this.formData.code_zone = "";
        }
        this.ProductTypeExists = false;
      }
    },

    handleCommodityTypeSelected(description) {
      const selected = this.commodityTypes.find(c => c.description === description);

      if (selected) {
        this.formData.type_commodity = selected.description;
        this.formData.code_type_commodity = selected.code;
        this.MerchandiseTypeExists = true;
      } else {
        this.formData.type_commodity = description;
        if (!this.formData.code_type_commodity) {
          this.formData.code_type_commodity = "";
        }
        this.MerchandiseTypeExists = false;
      }
    },

    handleSalesChannelSelected(description) {
      const selected = this.salesChannels.find(c => c.description === description);

      if (selected) {
        this.formData.name_location_seller = selected.description;
        this.formData.code_location_seller = selected.code_channel_WS;
        this.salesChannelExist = true;
      } else {
        this.formData.name_location_seller = "";
        this.salesChannelExist = true;
        this.formData.code_location_seller = "";
      }
    },

    handleBusinessUnitSelected(description) {
      const selected = this.businessUnits.find(b => b.description === description);

      if (selected) {
        this.formData.businessUnit = selected.description;
        this.formData.code_businessunit = selected.codeBusinessUnitWS;
        this.businessUnitExist = true;
      } else {
        this.formData.businessUnit = "";
        this.businessUnitExist = true;
        this.formData.code_businessunit = "";
      }
    },

    onLocationSelected(value) {
      const loc = this.locations.find(l => l.description === value);
      if (loc) {
        this.formData.description_location_destiny = loc.description;
        this.formData.code_location_destiny = loc.code_location;
        this.formData.latitude = loc.latitudeAddress;
        this.formData.longitude = loc.longitudeAddress;
        this.formData.code_city = loc.codeCity;
        this.formData.description_city = loc.nameCity;
        this.formData.address = loc.address || "";
        this.formData.code_deparment = loc.codeDepartment;
        this.formData.name_deparment = loc.nameDepartment;
        this.formData.code_country = loc.codeCountry;
        this.formData.name_country = loc.nameCountry;
        this.formData.id_location = loc.idLocation;
        this.formData.id_city = loc.idCity;
        
        // Set IDs for proper cascading
        this.idCountry = loc.idCountry || null;
        this.idDepartment = loc.idDepartment || null;
        
        // Load related data if needed
        if (this.idCountry && this.departments.length === 0) {
          this.loadDepartments();
        }
        if (this.idDepartment && this.cities.length === 0) {
          this.loadCities();
        }
        
        // Block fields when existing location is selected
        this.localityExists = true;
      } else {
        // Clear location-specific data but keep user input for new location
        this.formData.description_location_destiny = value || '';
        this.formData.code_location_destiny = '';
        this.formData.id_location = null;
        
        // Don't clear manually entered geographic data
        // this.formData.latitude = '';
        // this.formData.longitude = '';
        // this.formData.code_city = '';
        // this.formData.description_city = '';
        // this.formData.address = '';
        // this.formData.code_deparment = '';
        // this.formData.name_deparment = '';
        // this.formData.code_country = '';
        // this.formData.name_country = '';
        // this.formData.id_city = null;
        
        // Allow editing for new location
        this.localityExists = false;
      }
    },
    openProductModal() {
      this.currentProduct = {
        code_product: "", description_product: "", barcode: "", unit: "",
        amount_order: "", amount_deliver: "", routing_variable: ""
      };
      this.showProductForm = true;
    },
    saveProductFromModal(products) {
      this.formData.products.push(products);
    },
    editProduct(products, stopsIndex, productsIndex) {
      this.productEditing = { ...products };
      this.indexEditing = { stopsIndex, productsIndex };
      this.showEditProductModal = true;
    },
    onProductEdited(updatedProduct) {
      const { stopsIndex, productsIndex } = this.indexEditing;
      if (this.stops[stopsIndex]?.products) {
        this.$set(this.stops[stopsIndex].products, productsIndex, { ...updatedProduct });
      }
      this.indexEditing = null;
      this.showEditProductModal = false;
    },
    deleteProduct(stopsIndex, productsIndex) {
      this.stops[stopsIndex].products.splice(productsIndex, 1);
    },
    toggleShowMore(index) {
      const i = this.expandedStops.indexOf(index);
      if (i >= 0) this.expandedStops.splice(i, 1);
      else this.expandedStops.push(index);
    },
    resetForm() {
      this.formData = {
        number_order: "",
        address: "",
        businessUnit: "",
        code_businessunit: "",
        code_city: "",
        description_city: "",
        code_deparment: "",
        name_deparment: "",
        code_country: "",
        name_country: "",
        code_location_destiny: "",
        code_location_seller: "",
        code_zone: "",
        datetime_in_place: "",
        datetime_out_place: null,
        description_company_client: "",
        description_location_destiny: "",
        id_company_client: "",
        indications: "",
        latitude: "",
        longitude: "",
        name_location_seller: "",
        name_zone: "",
        remission: "",
        shipment_number: "",
        timezone: "UTC-05:00",
        type_commodity: "",
        type_company_client: "",
        description_type_delivery: "",
        type_location: "",
        zip: "",
        description_state_order: "",
        id_city: null,
        id_location: null,
        products: [],
        code_type_commodity: ""
      };
      
      // Reset IDs for cascading
      this.idCountry = null;
      this.idDepartment = null;
      
      // Reset arrays
      this.countries = [];
      this.departments = [];
      this.cities = [];
      
      // Reset flags
      this.localityExists = true;
      this.clientDescriptionExists = true;
      this.ProductTypeExists = true;
      this.MerchandiseTypeExists = true;
      this.salesChannelExist = true;
      this.businessUnitExist = true;
      
      this.currentProduct = {};
      this.showProductForm = false;
    },
  }
};
</script>

<style lang="scss" scoped src="../../TripCreateForm.module.scss" />